<template>
  <NuxtLayout
    back-route="/account"
    name="overlay"
    nav-icon="arrow-left"
    :title="$t('account.payout.headline')"
  >
    <section v-if="error" class="flex flex-col items-center gap-200 mt-400 text-center">
      <h1 class="headline-h4-headline">
        {{ $t('account.payout.accounts.error.headline') }}
      </h1>
      <h2 class="paragraph-regular">
        {{ $t('account.payout.accounts.error.description') }}
      </h2>

      <Illustration name="payout-backend-error" />

      <LcButton @click="reloadPage">
        {{ $t('error.page.general.button.reload') }}
      </LcButton>
    </section>

    <section
      v-else-if="noStoredAndInitialAccountsAvailable"
      class="flex flex-col items-center gap-200 mt-400 text-center"
    >
      <h1 class="headline-h4-headline">
        {{ $t('account.payout.accounts.noaccounts.headline') }}
      </h1>

      <p>{{ $t('account.payout.accounts.noaccounts.description') }}</p>

      <Illustration name="customer-success" />

      <LcButton href="/service?chat=true" variant="secondary">
        {{ $t('account.payout.accounts.noaccounts.cta') }}
      </LcButton>
    </section>

    <template v-else>
      <LcSkeleton v-if="loading" height="14.5rem" />

      <PayoutAmountForm
        v-else
        ref="payoutAmountForm"
        v-model:amount="selectedAmount"
        :max="maxPayableBalance"
        :min="minPayableBalance"
      />

      <section class="mt-200">
        <header class="mb-100">
          <h2 class="headline-h4-headline">
            {{ $t('account.payout.accounts.headline') }}
          </h2>
        </header>

        <LcSkeleton v-if="loading" height="14.5rem" />

        <PayoutMethodSelectorStored
          v-else-if="onlyStoredAccountsAvailable"
          v-model:selected-method="selectedMethodId"
          :accounts="storedAccounts"
        />

        <PayoutMethodSelectorNew
          v-else-if="onlyInitialAccountsAvailable"
          :accounts="initialAccounts"
          @manual-transfer-requested="handleManualTransferRequested"
        />

        <LcTabs v-else v-bind="{ tabs }" v-model:value="selectedOption" tab-flex-direction="row">
          <template #tab-panel="{ tab }">
            <div v-if="tab.name === 'stored'">
              <PayoutMethodSelectorStored
                v-model:selected-method="selectedMethodId"
                :accounts="storedAccounts"
              />
            </div>

            <div v-if="tab.name === 'new'">
              <PayoutMethodSelectorNew
                :accounts="initialAccounts"
                :stored-accounts="storedAccounts"
              />
            </div>
          </template>
        </LcTabs>
      </section>

      <TheNavbar v-if="!loading && selectedOption === 'stored' && !onlyInitialAccountsAvailable">
        <LcButton
          class="flex-grow"
          :loading="payoutToSavedAccountLoading"
          @click="invokePayoutToSavedAccount"
        >
          {{
            $t(
              'account.payout.accounts.button.payout',
              selectedAmount
                ? {
                    amount: formatMoney({
                      currency: 'USD',
                      value: selectedAmount,
                    }),
                  }
                : {},
            )
          }}
        </LcButton>
      </TheNavbar>
    </template>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers'

import { LcTabs } from '@lottocom/frontend-components'

import type { Money } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { PayoutAmountForm } from '#components'

definePageMeta({
  footer: false,
  layout: false,
  middleware: ['auth'],
})

const { navigateToLocalized } = useRouterUtils()
const { locale } = useI18n()

const PAYABLE_BALANCE_FALLBACK: Money = { currency: 'USD', value: 0 }

const { addToastError, removeAllToasts } = useToaster()
const { log } = useDatadog()
const { t } = useI18n()
const { onPayOutInstrumentAdded } = usePayoutSingleton()

const licensedTerritory = useLicensedTerritory({ raw: true })

const payoutAmountFormRef =
  useTemplateRef<ComponentExposed<typeof PayoutAmountForm>>('payoutAmountForm')

const { activeTabs: tabs } = useTabs(() => [
  { name: 'stored', title: t('account.payout.tabs.stored') },
  { name: 'new', title: t('account.payout.tabs.new') },
])

const {
  error,
  loading,
  onError,
  onResult,
  refetch: refetchPayoutAccountsQuery,
  result,
} = usePayOutAccountsQuery(
  { licensedTerritory, useManualAccountsOnly: false },
  { fetchPolicy: 'network-only' },
)

const { loading: payoutToSavedAccountLoading, mutate: payoutToSavedAccount } =
  usePayOutToSavedAccountMutation()

const selectedAmount = ref<number>()
const selectedMethodId = ref<string>()
const selectedOption = ref<'new' | 'stored'>('stored')

const isUseManualAccountsOnly = ref(false)
provide('isUseManualAccountsOnly', isUseManualAccountsOnly)

const initialAccounts = computed(() => result.value?.payOutAccounts.initialAccounts || [])

const maxPayableBalance = computed(
  () => result.value?.payOutAccounts.maxPayableBalance || PAYABLE_BALANCE_FALLBACK,
)

const minPayableBalance = computed(
  () => result.value?.payOutAccounts.minPayableBalance || PAYABLE_BALANCE_FALLBACK,
)

const noStoredAndInitialAccountsAvailable = computed(
  () =>
    !loading.value &&
    (result.value?.payOutAccounts.accounts.length ?? 0) <= 0 &&
    (result.value?.payOutAccounts.initialAccounts.length ?? 0) <= 0,
)

const onlyStoredAccountsAvailable = computed(
  () => (result.value?.payOutAccounts.initialAccounts.length ?? 0) <= 0,
)

const onlyInitialAccountsAvailable = computed(
  () => (result.value?.payOutAccounts.accounts.length ?? 0) <= 0,
)

const storedAccounts = computed(() => result.value?.payOutAccounts.accounts || [])

const invokePayoutToSavedAccount = async () => {
  const isPayoutAmountValid = (await payoutAmountFormRef.value?.validate())?.valid ?? false

  if (!isPayoutAmountValid || !selectedMethodId.value || !selectedAmount.value) return

  try {
    const result = await payoutToSavedAccount({
      input: {
        amount: { currency: 'USD', value: selectedAmount.value },
        idempotencyKey: crypto.randomUUID(),
        licensedTerritory,
        payOutAccountId: selectedMethodId.value,
      },
    })

    const amount = result?.data?.payOutToSavedAccount.amount
    if (!amount) {
      showPayoutErrorToast(undefined)
      return
    }

    return await navigateToLocalized(
      {
        path: `/account/payout/success/${selectedMethodId.value}`,
        query: {
          amount: amount.value,
          currency: amount.currency,
          manualAccountsOnly: isUseManualAccountsOnly.value.toString(),
        },
      },
      { replace: true },
      locale.value,
    )
  } catch (error) {
    showPayoutErrorToast(error)
  }
}

const handleManualTransferRequested = () => {
  isUseManualAccountsOnly.value = true
  refetchPayoutAccountsQuery({
    licensedTerritory,
    useManualAccountsOnly: isUseManualAccountsOnly.value,
  })
}

const reloadPage = () => {
  window.location.reload()
}

const showPayoutErrorToast = (error: unknown) => {
  log('error', {
    component: 'account/payout/index.vue',
    message: 'Failed to payout',
    messageContext: { error },
  })

  addToastError(
    {
      error,
      prefix: 'account.payout.toast.error',
      tracking: {
        action: 'payout',
        topicKey: 'payout_to_saved_account_failed',
      },
    },
    {
      fallback: {
        headline: t('account.payout.toast.error.fallback.message'),
        message: t('account.payout.toast.error.fallback.headline'),
        type: 'danger',
      },
    },
  )
}

onError((error) => {
  log('error', {
    component: 'account/payout/index.vue',
    message: 'Failed to load payout accounts',
    messageContext: { error },
  })
})

onPayOutInstrumentAdded((id?: string) => {
  if (id) selectedMethodId.value = id
  selectedOption.value = 'stored'
})

onResult(({ data }) => {
  const firstStoredAccountId = data.payOutAccounts.accounts?.[0]?.id
  if (!firstStoredAccountId) return
  selectedMethodId.value = firstStoredAccountId
})

onBeforeUnmount(removeAllToasts)
</script>
